import React, { useRef, useState } from 'react';
import { Banner, Button, Picker, TextField, Typography } from '@riac/design-system';
import { Item } from '@react-stately/collections';
//@ts-expect-error
import { useTurnstile, useTurnstileRender } from '@riac/core/turnstile';

interface IAddCreditCardCountry {
  CountryID: number;
  CountryName: string;
  CountryCode: string;
}

export interface IAddCreditCardFormProps {
  countries: IAddCreditCardCountry[];
  onAddCard: () => void;
  handleCancel: () => void;
}

interface FormData {
  cardholderName: string;
  cardNumber: string;
  expiration: string;
  cvv: string;
  country: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  cardNickname: string;
}

interface FormErrors {
  cardholderName?: string;
  cardNumber?: string;
  expiration?: string;
  cvv?: string;
  country?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  cardNickname?: string;
}

export const AddCreditCardForm = ({ countries, onAddCard, handleCancel }: IAddCreditCardFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    cardholderName: '',
    cardNumber: '',
    expiration: '',
    cvv: '',
    country: '236', // Default to US
    address: '',
    city: '',
    state: '',
    zipCode: '',
    cardNickname: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [verify, setVerify] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [authErrors, setAuthErrors] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState('');

  const [turnstileToken, setTurnstileToken] = useState<any>();

  const turnstile = useTurnstile();
  const turnstileContainerRef = useRef();

  const widgetManager = useTurnstileRender(
    turnstile,
    window.TURNSTILE_SITE_KEY,
    turnstileContainerRef,
    (token) => setTurnstileToken(token),
  );

  const handleChange = (name, value) => {
    let formattedValue = value;

    if (name === 'cardNumber') {
      // Remove all non-digit characters
      formattedValue = value.replace(/\D/g, '');
      
      // Check if it's an AMEX card (starts with 34 or 37)
      const isAmex = /^3[47]/.test(formattedValue);
      
      if (isAmex) {
        // Format AMEX: XXXX XXXXXX XXXXX
        formattedValue = formattedValue.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
      } else {
        // Format other cards: XXXX XXXX XXXX XXXX
        formattedValue = formattedValue.replace(/(\d{4})(?=\d)/g, '$1 ');
      }
      formattedValue = formattedValue.trim();
    } else if (name === 'expiration') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length > 2) {
        formattedValue = formattedValue.slice(0, 2) + '/' + formattedValue.slice(2, 4);
      }
    } else if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: formattedValue,
    }));
  };

  const validatePostalCode = (postalCode: string, countryCode: string): boolean => {
    const postalCodeRegexes = {
      US: /^\d{5}(-\d{4})?$/,
      CA: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] \d[ABCEGHJ-NPRSTV-Z]\d$/,
      GB: /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/,
    };

    // We could specify more countries here e.g. from https://github.com/unicode-org/cldr/blob/release-26-0-1/common/supplemental/postalCodeData.xml
    // But I think it's better to keep it simple. w/ a general regex for other countries and allowing blank postal codes for them.

    const defaultRegex = /^[A-Z0-9 -]{1,10}$/i;
    if(!postalCodeRegexes[countryCode] && (!postalCode || postalCode.length === 0)) {
      return true;
    }

    const regex = postalCodeRegexes[countryCode] || defaultRegex;
    return regex.test(postalCode);
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;

    if (!formData.cardholderName.trim()) {
      newErrors.cardholderName = 'Cardholder name is required';
    }
    
    const cardNumber = formData.cardNumber.replace(/\s/g, '');
    const isAmex = /^3[47]\d{13}$/.test(cardNumber);
    const isOtherCard = /^\d{16}$/.test(cardNumber);
    if (!isAmex && !isOtherCard) {
      newErrors.cardNumber = 'Invalid card number';
    }

    if (!/^\d{2}\/\d{2}$/.test(formData.expiration)) {
      newErrors.expiration = 'Invalid expiration date (MM/YY)';
    } else {
      const [month, year] = formData.expiration.split('/').map(Number);
      if (month < 1 || month > 12) {
        newErrors.expiration = 'Invalid month';
      } else if (year < currentYear || (year === currentYear && month < currentMonth)) {
        newErrors.expiration = 'Card has expired';
      }
    }

    // Validate CVV (3 digits for regular cards, 4 for AMEX)
    const cvvRegex = isAmex ? /^\d{4}$/ : /^\d{3}$/;
    if (!cvvRegex.test(formData.cvv)) {
      newErrors.cvv = `Invalid CVV (${isAmex ? '4' : '3'} digits required)`;
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }
    if (!formData.address) {
      newErrors.address = 'Address is required';
    }
    if (!formData.city) {
      newErrors.city = 'City is required';
    }

    const selectedCountry = countries.find(c => c.CountryID.toString() === formData.country);
    const countryCode = selectedCountry ? selectedCountry.CountryCode : 'US';

    if (!validatePostalCode(formData.zipCode, countryCode)) {
      newErrors.zipCode = 'Invalid postal code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addCard = (e) => {
    e.preventDefault();
    const creditCardNumber = formData.cardNumber.replace(/\s/g, '');
    const cc4 = creditCardNumber.slice(-4);
    const validated = validateForm();
    if (!validated) {
      return;
    }
    setAuthErrorMessage('');
    setAuthErrors(false);
    setVerify(true);
    fetch('/api/account/creditcard/addCard', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fullName: formData.cardholderName,
        cardNumber: creditCardNumber,
        cardExpiration: formData.expiration,
        cardCode: formData.cvv,
        streetAddress: formData.address,
        city: formData.city,
        state: formData.state,
        postalCode: formData.zipCode,
        last4: cc4,
        cardNickname: formData.cardNickname,
        country: formData.country,
        turnstileToken,
      }),
    }).then((response) => response.json())
      .then((e) => {
        if (e.success) {
          setVerify(false);
          setConfirm(true);
          setFormData({
            cardholderName: '',
            cardNumber: '',
            expiration: '',
            cvv: '',
            country: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            cardNickname: '',
          });
          console.log(e);
          onAddCard();
        } else {
          setAuthErrorMessage(e.message);
          setVerify(false);
          setConfirm(false);
          setAuthErrors(true);
          let message = e.message + ', please confirm your card information';
          if (typeof e.errors !== 'undefined') {
            message = e.message;
          }
          setAuthErrorMessage(message);
          widgetManager.reset();
        }
      });
  };

  return (
    <form onSubmit={addCard}>
      <div className='flex flex-col gap-9' style={{maxWidth: '540px'}}>
        <div className='flex flex-col gap-3'>
          <Typography tag='h2' heading weight='bold' size='sm'>Card details</Typography>
          <div>
            <TextField
              label='Cardholder Name'
              name='cardholderName'
              value={formData.cardholderName}
              onChange={(value) => handleChange('cardholderName', value)}
              isDisabled={verify}
              errorMessage={errors.cardholderName}
            />
          </div>
          <div>
            <TextField
              id='cardNumber'
              name='cardNumber'
              label='Card Number'
              value={formData.cardNumber}
              onChange={(e) => handleChange('cardNumber', e)}
              maxLength={19}
              isDisabled={verify}
              errorMessage={errors.cardNumber}
            />
          </div>
          <div className='flex gap-3 flex-wrap'>
            <TextField
              label='Expiration (MM/YY)'
              name='expiration'
              value={formData.expiration}
              onChange={(value) => handleChange('expiration', value)}
              maxLength={5}
              isDisabled={verify}
              errorMessage={errors.expiration}
              className='min-w-40 basis-40 flex-1'
            />
            <TextField
              label='CVV'
              name='cvv'
              value={formData.cvv}
              onChange={(value) => handleChange('cvv', value)}
              isDisabled={verify}
              errorMessage={errors.cvv}
              className='min-w-40 basis-40 flex-[10]'
            />
          </div>
          <div>
            <TextField
              label='Card Nickname (Optional)'
              name='cardNickname'
              value={formData.cardNickname}
              onChange={(value) => handleChange('cardNickname', value)}
              isDisabled={verify}
            />
          </div>
        </div>
        <div className='flex flex-col gap-3'>
          <Typography tag='h2' heading weight='bold' size='sm'>Billing address</Typography>
          <div>
            <TextField
              label='Address'
              name='address'
              value={formData.address}
              onChange={(value) => handleChange('address', value)}
              isDisabled={verify}
              errorMessage={errors.address}
            />
          </div>
          <div className='flex gap-4 flex-wrap'>
            <TextField
              label='City'
              name='city'
              value={formData.city}
              onChange={(value) => handleChange('city', value)}
              isDisabled={verify}
              errorMessage={errors.city}
              className='flex-1 min-w-32 basis-32'
            />
            <TextField
              label='State/Province'
              name='state'
              value={formData.state}
              onChange={(value) => handleChange('state', value)}
              isDisabled={verify}
              errorMessage={errors.state}
              className='flex-1 min-w-32 basis-32'
            />
            <TextField
              label='Postal Code'
              name='zipCode'
              value={formData.zipCode}
              onChange={(value) => handleChange('zipCode', value)}
              isDisabled={verify}
              errorMessage={errors.zipCode}
              className='flex-1 min-w-32 basis-32'
            />
          </div>
          <div className='flex flex-col'>
            <Picker
              label='Country'
              name='country'
              selectedKey={formData.country}
              onSelectionChange={(key) => handleChange('country', key)}
              isDisabled={verify}
              position={'top'}
            >
              {countries.map((country) => (
                <Item key={country.CountryID}>
                  {country.CountryName}
                </Item>
              ))}
            </Picker>
            {errors.country && <span className="text-red-500">{errors.country}</span>}
          </div>
        </div>
        <div className='flex flex-col gap-2'>
          <div ref={turnstileContainerRef}></div>
          <Button size='lg' onPress={addCard} isDisabled={verify || (!turnstileToken)}>
            {verify ? 'Verifying...' : 'Add Card'}
          </Button>
          <Button size='lg' variant='secondary' onPress={() => handleCancel()}>Cancel</Button>
          <Typography size='lg'>
            {Object.keys(errors).length > 0 && <Banner color='error'>Please fix the errors above</Banner>}
            {authErrors && <Banner color='error'>{authErrorMessage}</Banner>}
          </Typography>
          {confirm && <Banner color='success'>Card added successfully!</Banner>}
        </div>
      </div>
    </form>
  );
};
